import type { ReactNode } from "react";
import type { CustomerBasketView } from "@shared-core/data/customer-basket-types";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { OrderingBlockedNotice } from "../OrderingBlockedNotice";
import { usePortalViewer } from "../PortalViewerContext";
import { useCustomerBasket } from "./CustomerBasketProvider";
import { AlertTriangle } from "lucide-react";

// What the basket and checkout pages show before there is a basket to show: no basket in this build, a person
// who may not order, the first read, or a read that failed. Past those, the page gets the basket as read and a
// way to read it again after a write.
export function BasketGate({
  children,
}: {
  children: (basket: CustomerBasketView, refresh: () => void) => ReactNode;
}) {
  const basket = useCustomerBasket();
  const { sessionState } = usePortalViewer();

  if (sessionState.status === "loading") return <LoadingCard />;
  if (
    sessionState.status === "success" &&
    !sessionState.data.can_order_by_card
  ) {
    return <OrderingBlockedNotice reason="no_capability" />;
  }
  if (!basket) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        The basket isn&apos;t available yet.
      </Card>
    );
  }
  if (basket.state.status === "loading") return <LoadingCard />;
  if (basket.state.status === "error") {
    return (
      <Card className="flex flex-col items-center gap-2 p-8 text-center">
        <AlertTriangle className="size-6 text-muted-foreground" />
        <p className="font-medium">We couldn&apos;t load your basket</p>
        <p className="text-sm text-muted-foreground">{basket.state.message}</p>
        <Button variant="outline" size="sm" onClick={basket.refresh}>
          Try again
        </Button>
      </Card>
    );
  }
  return <>{children(basket.state.data, basket.refresh)}</>;
}

function LoadingCard() {
  return (
    <Card className="p-8 text-center text-sm text-muted-foreground">
      Loading…
    </Card>
  );
}
